// The receive-a-whole-order contract module. Every API shape the order receive
// page relies on is typed and fetched here; paths carry no /api prefix because
// config.API_ENDPOINT already ends in it.

import { useApi } from '../apiHelpers';
import type { PendingSlipLine } from './packingListApi';
import type {
  ExpectedReceiptRow,
  LineReceiptItem,
  LocationRow,
  OrderReceiveLine,
  OverReceiptLine,
  ReceiveDeliveryRequest,
  ReceiveDeliveryResult,
} from './types';

export type {
  OrderReceiveLine,
  OverReceiptLine,
  ReceiveDeliveryRequest,
  ReceiveDeliveryResult,
};

/** GET /expected-receipts/:id: the row plus what has been received against it. */
export type SingleLineDetail = ExpectedReceiptRow & {
  receipt_items?: LineReceiptItem[];
};

/** The refusal a 409 over_receipt_needs_approval carries, read off the error. */
export interface OverReceiptRefusal {
  po_number: string | null;
  lines: OverReceiptLine[];
}

/**
 * The structured error body, wherever the server put it. apiFetchAuthed hangs
 * the parsed JSON on `err.data`; the receive routes nest it under `error`.
 */
export function errorBody(err: any): Record<string, any> | null {
  const data = err?.data;
  if (!data) return null;
  if (data.error && typeof data.error === 'object') return data.error;
  return data;
}

export function errorCode(err: any): string | null {
  const body = errorBody(err);
  return typeof body?.code === 'string' ? body.code : null;
}

/** The over-receipt refusal, or null when this error is something else. */
export function overReceiptRefusal(err: any): OverReceiptRefusal | null {
  if (err?.status !== 409 || errorCode(err) !== 'over_receipt_needs_approval') return null;
  const body = errorBody(err) || {};
  const lines = Array.isArray(body.lines) ? body.lines : Array.isArray(err?.data?.lines) ? err.data.lines : [];
  return {
    po_number: body.po_number ?? err?.data?.po_number ?? null,
    lines: lines as OverReceiptLine[],
  };
}

export function useReceiveDeliveryApi() {
  const api = useApi();

  return {
    /** Every stock line of one order, received and cancelled included. */
    orderLines: async (poId: string): Promise<OrderReceiveLine[]> => {
      const data = (await api.get(
        `/expected-receipts?purchase_order_id=${encodeURIComponent(poId)}`
      )) as { rows?: OrderReceiveLine[] } | OrderReceiveLine[];
      const rows = Array.isArray(data) ? data : data?.rows || [];
      return rows.map((r) => ({ ...r, receipt_items: r.receipt_items || [] }));
    },

    /** One line, for the headerless page. */
    line: (lineId: string): Promise<SingleLineDetail> =>
      api.get(`/expected-receipts/${encodeURIComponent(lineId)}`),

    /** Confirmed slip lines still waiting for boxes, every order's. */
    pendingSlipLines: async (): Promise<PendingSlipLine[]> => {
      const data = (await api.get('/procurement/packing-list/pending-lines')) as { rows?: PendingSlipLine[] };
      return data?.rows || [];
    },

    receivableLocations: async (): Promise<LocationRow[]> =>
      ((await api.get('/locations?receivable=1')) as LocationRow[]) || [],

    receive: (body: ReceiveDeliveryRequest): Promise<ReceiveDeliveryResult> =>
      api.post('/inventory/receive-delivery', { body }),
  };
}
